import type { User } from '@/core/auth/types';
import {
  canViewBookings,
  canViewGuests,
  canViewAccommodations,
  canViewActivities,
  canManagePOS,
  canManageInventory,
  canManageSuppliers,
  canViewStaffModule,
  canViewShifts,
  canManageLocationSettings,
} from '@/core/auth/modulePermissions';
import { canViewCompensation } from '@/modules/hr/utils/compensationVisibility';
import { canViewTreasury } from '@/modules/treasury/utils/permissions';

// Last visited location-overview tab, held in memory for the session. Browser
// storage is prohibited workspace-wide; this resets on a full reload, which is
// acceptable for a navigation convenience (it falls back to the default).
let lastOverviewPath: string | null = null;

/**
 * Default landing for a location-scope session. The previous default
 * was `/management/overview`, which on 2026-04-25 was moved to company
 * scope (`/company/overview`) — that page is no longer reachable from
 * the location rail. Frontdesk is the canonical location entry point.
 */
export const DEFAULT_LOCATION_OVERVIEW_PATH = '/location/frontdesk/overview';

const OVERVIEW_PATHS = [
  '/location/frontdesk/overview',
  '/location/products-sales/overview',
  '/location/pos/overview',
  '/location/hr/overview',
  '/location/treasury/overview',
] as const;

const LOCATION_ROUTE_GROUPS: Array<{
  overview: (typeof OVERVIEW_PATHS)[number];
  prefixes: string[];
}> = [
  {
    overview: '/location/frontdesk/overview',
    prefixes: ['/location/frontdesk', '/location/bookings', '/location/guests', '/persons', '/location/rooms', '/location/activities'],
  },
  {
    overview: '/location/products-sales/overview',
    prefixes: ['/location/products-sales', '/location/bar-kitchen', '/location/shop', '/location/sales'],
  },
  {
    overview: '/location/pos/overview',
    prefixes: ['/location/pos'],
  },
  {
    overview: '/location/hr/overview',
    prefixes: ['/location/hr', '/location/staff', '/location/shifts', '/location/payroll'],
  },
  {
    overview: '/location/treasury/overview',
    prefixes: ['/location/treasury', '/location/suppliers'],
  },
];

function matchesPrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function resolveLocationOverviewPath(pathname: string) {
  const match = LOCATION_ROUTE_GROUPS.find((group) =>
    group.prefixes.some((prefix) => matchesPrefix(pathname, prefix)),
  );

  return match?.overview ?? null;
}

/**
 * Resolve the tail (`frontdesk/overview`, `hr/overview`, …) of the domain
 * overview a freshly-landed user should be dropped on for a given location.
 *
 * The old behaviour hardcoded `frontdesk/overview` for every role, so
 * HR / Commerce / Treasury users landed on a Frontdesk dashboard they have
 * no permission to operate (it fired booking/room queries that 403'd). This
 * routes each role to the first domain it can actually see.
 *
 * Frontdesk stays the *preferred* home whenever the user has any Frontdesk
 * permission (bookings / guests / rooms / activities) — this keeps owners and
 * front-desk staff on the exact page they land on today. Only roles with no
 * Frontdesk access fall through to their real domain, in rail order:
 * Commerce → Team → Treasury → Management.
 *
 * Permission-only (no per-location module flags): this runs in the sync
 * post-login redirect (GatewayPage / RootEntryPage) before location module
 * config is available. RBAC is the meaningful gate; a role holding a module
 * permission while that module is disabled on the location is degenerate and
 * the target overview's own ModuleProtectedPage still guards it.
 */
export function resolveDefaultLocationOverviewTail(
  user: User | null | undefined,
  locationId?: string | null,
): string {
  const canFrontdesk =
    canViewBookings(user, locationId)
    || canViewGuests(user, locationId)
    || canViewAccommodations(user, locationId)
    || canViewActivities(user, locationId);
  if (canFrontdesk) return 'frontdesk/overview';

  const canCommerce =
    canManagePOS(user, locationId)
    || canManageInventory(user, locationId)
    || canManageSuppliers(user, locationId);
  if (canCommerce) return 'products-sales/overview';

  const canTeam =
    canViewStaffModule(user, locationId)
    || canViewShifts(user, locationId)
    || canViewCompensation(user, locationId);
  if (canTeam) return 'hr/overview';

  if (canViewTreasury(user, 'location', locationId)) return 'treasury/overview';

  if (canManageLocationSettings(user, locationId)) return 'management/overview';

  // No location-domain permission resolved — keep the historical default so
  // the user lands on a real (now query-gated, 403-free) page rather than a
  // dead route.
  return 'frontdesk/overview';
}

export function rememberLocationOverviewPath(pathname: string) {
  const overviewPath = resolveLocationOverviewPath(pathname);
  if (overviewPath) {
    lastOverviewPath = overviewPath;
  }
}

export function readLastLocationOverviewPath() {
  return lastOverviewPath
    && OVERVIEW_PATHS.includes(lastOverviewPath as (typeof OVERVIEW_PATHS)[number])
    ? lastOverviewPath
    : DEFAULT_LOCATION_OVERVIEW_PATH;
}
